import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** @deprecated Use `onInk` for dark/ink surfaces. */
  tone?: 'default' | 'ink'
  /** Canonical on-ink spelling; maps to `tone="ink"`. */
  onInk?: boolean
  badge?: boolean
  children?: ReactNode
}

/** Square icon-only button. Always pass an aria-label. Use `onInk` for dark surfaces. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { tone = 'default', onInk, badge = false, className = '', children, ...props },
  ref,
) {
  const tones = {
    default: 'text-ink-muted hover:bg-whisper hover:text-strong focus-visible:shadow-focus',
    ink: 'text-on-ink-soft hover:bg-veil hover:text-on-ink focus-visible:shadow-focus-dark',
  }
  const resolvedTone = (onInk ?? tone === 'ink') ? 'ink' : 'default'

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'relative inline-flex items-center justify-center min-w-tap min-h-tap rounded-control transition-colors duration-fast ease-out-soft active:translate-y-px focus-visible:outline-none',
        tones[resolvedTone],
        className,
      )}
      {...props}
    >
      {children}
      {badge && (
        <span
          className="absolute top-2 right-2 w-[7px] h-[7px] rounded-full bg-mustard ring-2 ring-ink"
          aria-hidden="true"
        />
      )}
    </button>
  )
})
